import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from './LanguageContext';
import { fetchWithAuth } from '../utils/api';
import Odontogram from './Odontogram';
import {
  Users, Clock, AlertTriangle, Phone, CreditCard, User,
  Play, CheckCircle, RefreshCw, ChevronLeft, Stethoscope,
  AlertCircle, FileText, Calendar, Activity, UserPlus, Search, X
} from 'lucide-react';

const DentistQueue = ({ currentBranch, user }) => {
  const { print_lang, currentLanguage } = useLanguage();

  // Queue data
  const [queue, setQueue] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Selected patient for treatment
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);

  // Treatment modal
  const [showTreatmentModal, setShowTreatmentModal] = useState(false);
  const [treatments, setTreatments] = useState([]);
  const [selectedTreatments, setSelectedTreatments] = useState([]);
  const [treatmentNotes, setTreatmentNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Walk-in modal
  const [showWalkInModal, setShowWalkInModal] = useState(false);
  const [patients, setPatients] = useState([]);
  const [dentists, setDentists] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [walkInForm, setWalkInForm] = useState({
    patient_id: '',
    dentist_id: '',
    evtype_id: '',
    notes: ''
  });
  const [isCreatingWalkIn, setIsCreatingWalkIn] = useState(false);

  // New patient registration in walk-in
  const [showNewPatientForm, setShowNewPatientForm] = useState(false);
  const [newPatientForm, setNewPatientForm] = useState({
    name: '',
    phone: '',
    citizen_id: '',
    gender: '',
    dob: '',
    address: '',
    drug_allergy: '',
    medical_history: ''
  });
  const [isCreatingPatient, setIsCreatingPatient] = useState(false);
  const [isReadingCard, setIsReadingCard] = useState(false);

  // Auto-refresh interval (30 seconds)
  const REFRESH_INTERVAL = 30000;

  // Fetch waiting queue
  const fetchQueue = useCallback(async () => {
    if (!currentBranch?.branch_id) {
      // console.log('DentistQueue: No branch_id, skipping fetch. currentBranch:', currentBranch);
      return;
    }

    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        branch_id: currentBranch.branch_id
      });

      // If user is a dentist, filter by their dentist_id
      if (user?.role === 'dentist' && user?.dentist_id) {
        params.append('dentist_id', user.dentist_id);
      }

      // console.log('DentistQueue: Fetching queue with params:', params.toString());

      const response = await fetchWithAuth(`/api/appointments/today/waiting?${params}`);
      const queueData = response.data || [];
      // console.log('DentistQueue: Received', queueData.length, 'appointments for branch', currentBranch.branch_id);
      setQueue(queueData);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to fetch queue:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentBranch, user]);

  // Fetch treatments for selection
  const fetchTreatments = async () => {
    try {
      // console.log('DentistQueue: Fetching treatments');
      const data = await fetchWithAuth('/api/treatments?status=active');
      // console.log('DentistQueue: Treatments loaded:', data?.length || 0);
      setTreatments(data || []);
    } catch (error) {
      console.error('Failed to fetch treatments:', error);
    }
  };

  // Fetch patients for walk-in selection
  const fetchPatients = async (search = '') => {
    if (!currentBranch?.branch_id) return;
    try {
      const params = new URLSearchParams({
        branch_id: currentBranch.branch_id,
        status: 'active'
      });
      if (search) params.append('search', search);
      const response = await fetchWithAuth(`/api/patients?${params}`);
      setPatients(response.data || response || []);
    } catch (error) {
      console.error('Failed to fetch patients:', error);
    }
  };

  // Fetch dentists for walk-in selection
  const fetchDentists = async () => {
    if (!currentBranch?.branch_id) return;
    try {
      const data = await fetchWithAuth(`/api/appointments/dentists/selection?branch_id=${currentBranch.branch_id}`);
      setDentists(data || []);
    } catch (error) {
      console.error('Failed to fetch dentists:', error);
    }
  };

  // Fetch appointment types
  const fetchAppointmentTypes = async () => {
    try {
      const data = await fetchWithAuth('/api/appointments/types/all');
      // Filter out "Day Off" type
      setAppointmentTypes((data || []).filter(t => t.type_name !== 'Day Off'));
    } catch (error) {
      console.error('Failed to fetch appointment types:', error);
    }
  };

  // Open walk-in modal
  const openWalkInModal = () => {
    fetchPatients();
    fetchDentists();
    fetchAppointmentTypes();
    // If user is dentist, pre-select their dentist_id
    if (user?.role === 'dentist' && user?.dentist_id) {
      setWalkInForm(prev => ({ ...prev, dentist_id: user.dentist_id }));
    }
    setShowWalkInModal(true);
  };

  // Close walk-in modal
  const closeWalkInModal = () => {
    setShowWalkInModal(false);
    setShowNewPatientForm(false);
    setPatientSearch('');
    setWalkInForm({
      patient_id: '',
      dentist_id: user?.role === 'dentist' ? user.dentist_id : '',
      evtype_id: '',
      notes: ''
    });
    setNewPatientForm({
      name: '',
      phone: '',
      citizen_id: '',
      gender: '',
      dob: '',
      address: '',
      drug_allergy: '',
      medical_history: ''
    });
  };

  // SmartCard Helper Functions
  const convertBuddhistDate = (dateStr) => {
    if (!dateStr) return '';
    const cleanDate = dateStr.toString().replace(/[\s-]/g, '');
    let year, month, day;

    if (cleanDate.includes('/')) {
      const parts = cleanDate.split('/');
      day = parseInt(parts[0]);
      month = parseInt(parts[1]);
      year = parseInt(parts[2]) - 543;
    } else if (cleanDate.length === 8) {
      year = parseInt(cleanDate.substring(0, 4)) - 543;
      month = parseInt(cleanDate.substring(4, 6));
      day = parseInt(cleanDate.substring(6, 8));
    } else {
      return '';
    }

    if (isNaN(year) || isNaN(month) || isNaN(day)) return '';
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const extractPrefixAndName = (fullName) => {
    const prefixes = ['นาย', 'นาง', 'นางสาว', 'เด็กชาย', 'เด็กหญิง', 'ด.ช.', 'ด.ญ.'];
    for (const prefix of prefixes) {
      if (fullName.startsWith(prefix)) {
        return { prefix, name: fullName.substring(prefix.length).trim() };
      }
    }
    return { prefix: '', name: fullName.trim() };
  };

  // Read SmartCard
  const handleReadSmartcard = async () => {
    setIsReadingCard(true);
    try {
      const response = await fetch('http://localhost:8081/json');
      if (!response.ok) {
        throw new Error('Failed to read smartcard');
      }

      const data = await response.json();
      // console.log('Smartcard data:', data);

      // Extract prefix and name from NameTH
      const { name } = extractPrefixAndName(data.NameTH || '');

      // Map gender
      let gender = '';
      if (data.Sex === 'ชาย') gender = 'male';
      else if (data.Sex === 'หญิง') gender = 'female';

      // Convert date of birth
      const dob = convertBuddhistDate(data.DoB);

      // Update form data
      setNewPatientForm(prev => ({
        ...prev,
        citizen_id: data.CitizenID || '',
        name: name,
        gender: gender,
        dob: dob,
        address: (data.Address || '').trim()
      }));

    } catch (error) {
      console.error('Failed to read smartcard:', error);
      alert(print_lang('smartcard_error', 'Failed to read smartcard. Please make sure the card reader is connected and the card is inserted.'));
    } finally {
      setIsReadingCard(false);
    }
  };

  // Create new patient
  const createNewPatient = async () => {
    if (!newPatientForm.name.trim()) {
      alert(print_lang('name_required', 'Patient name is required'));
      return;
    }

    setIsCreatingPatient(true);
    try {
      const response = await fetchWithAuth('/api/patients', {
        method: 'POST',
        body: JSON.stringify({
          branch_id: currentBranch.branch_id,
          name: newPatientForm.name.trim(),
          phone: newPatientForm.phone.trim(),
          citizen_id: newPatientForm.citizen_id.trim(),
          gender: newPatientForm.gender,
          dob: newPatientForm.dob || null,
          address: newPatientForm.address.trim(),
          drug_allergy: newPatientForm.drug_allergy.trim(),
          medical_history: newPatientForm.medical_history.trim(),
          status: 'active'
        })
      });

      // Get new patient ID and add to list
      const newPatientId = response.patient_id || response.id;
      if (newPatientId) {
        // Refresh patients list and select the new patient
        await fetchPatients();
        setWalkInForm(prev => ({ ...prev, patient_id: String(newPatientId) }));
        setShowNewPatientForm(false);
        setNewPatientForm({
          name: '',
          phone: '',
          citizen_id: '',
          gender: '',
          dob: '',
          address: '',
          drug_allergy: '',
          medical_history: ''
        });
      }
    } catch (error) {
      console.error('Failed to create patient:', error);
      alert(error.message || print_lang('error_creating_patient', 'Failed to create patient'));
    } finally {
      setIsCreatingPatient(false);
    }
  };

  // Create walk-in appointment
  const createWalkInAppointment = async () => {
    if (!walkInForm.patient_id) {
      alert(print_lang('select_patient_required', 'Please select a patient'));
      return;
    }
    if (!walkInForm.dentist_id) {
      alert(print_lang('select_dentist_required', 'Please select a dentist'));
      return;
    }

    setIsCreatingWalkIn(true);
    try {
      // Create appointment starting now
      const now = new Date();
      const endTime = new Date(now.getTime() + 60 * 60 * 1000); // 1 hour duration

      const selectedPatient = patients.find(p => p.patient_id === parseInt(walkInForm.patient_id));

      await fetchWithAuth('/api/appointments', {
        method: 'POST',
        body: JSON.stringify({
          branch_id: currentBranch.branch_id,
          patient_id: walkInForm.patient_id,
          dentist_id: walkInForm.dentist_id,
          evtype_id: walkInForm.evtype_id || null,
          text: walkInForm.notes || `Walk-in: ${selectedPatient?.name || 'Patient'}`,
          start_date: now.toISOString(),
          end_date: endTime.toISOString(),
          status: 'checked_in' // Walk-in patients are already here
        })
      });

      closeWalkInModal();
      fetchQueue();
      alert(print_lang('walkin_created', 'Walk-in patient added to queue'));
    } catch (error) {
      console.error('Failed to create walk-in:', error);
      alert(error.message || print_lang('error_creating_walkin', 'Failed to create walk-in appointment'));
    } finally {
      setIsCreatingWalkIn(false);
    }
  };

  // Auto-refresh
  useEffect(() => {
    fetchQueue();
    fetchTreatments();

    const interval = setInterval(fetchQueue, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchQueue]);

  // Update appointment status
  const updateStatus = async (appointmentId, newStatus) => {
    try {
      await fetchWithAuth(`/api/appointments/${appointmentId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });
      fetchQueue();
    } catch (error) {
      console.error('Failed to update status:', error);
      alert(error.message);
    }
  };

  // Start treatment (change status to in_progress and open odontogram)
  const startTreatment = async (appointment) => {
    if (appointment.status !== 'in_progress') {
      await updateStatus(appointment.id, 'in_progress');
    }
    setSelectedAppointment(appointment);
    setSelectedPatient({
      patient_id: appointment.patient_id,
      name: appointment.patient_name,
      phone: appointment.patient_phone,
      citizen_id: appointment.citizen_id,
      drug_allergy: appointment.drug_allergy,
      medical_history: appointment.medical_history
    });
  };

  // Finish treatment
  const finishTreatment = () => {
    setShowTreatmentModal(true);
  };

  // Submit completed treatment
  const submitTreatment = async () => {
    if (selectedTreatments.length === 0) {
      alert(print_lang('select_treatment_required', 'Please select at least one treatment'));
      return;
    }

    setIsSaving(true);
    try {
      // Create treatment records for each selected treatment
      for (const treatment of selectedTreatments) {
        await fetchWithAuth('/api/treatment-records', {
          method: 'POST',
          body: JSON.stringify({
            patient_id: selectedAppointment.patient_id,
            treatment_id: treatment.t_id,
            dentist_id: user?.dentist_id,
            date: new Date().toISOString().split('T')[0],
            notes: treatmentNotes,
            cost: treatment.price,
            status: 'completed'
          })
        });
      }

      // Update appointment status to completed
      await updateStatus(selectedAppointment.id, 'completed');

      // Reset state
      setShowTreatmentModal(false);
      setSelectedTreatments([]);
      setTreatmentNotes('');
      setSelectedAppointment(null);
      setSelectedPatient(null);

      alert(print_lang('treatment_completed', 'Treatment completed and sent to billing'));
    } catch (error) {
      console.error('Failed to submit treatment:', error);
      alert(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Back to queue
  const backToQueue = () => {
    setSelectedAppointment(null);
    setSelectedPatient(null);
  };

  // Get status badge style and text
  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: {
        bg: 'bg-gray-100',
        text: 'text-gray-700',
        label: print_lang('scheduled', 'Scheduled'),
        icon: Calendar
      },
      checked_in: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-700',
        label: print_lang('waiting', 'Waiting'),
        icon: Clock
      },
      in_progress: {
        bg: 'bg-blue-100',
        text: 'text-blue-700',
        label: print_lang('in_progress', 'In Progress'),
        icon: Activity
      },
      completed: {
        bg: 'bg-green-100',
        text: 'text-green-700',
        label: print_lang('completed', 'Completed'),
        icon: CheckCircle
      }
    };
    return statusConfig[status] || statusConfig.scheduled;
  };

  // Format time
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString(currentLanguage === 'th' ? 'th-TH' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate waiting time from appointment start time
  const calculateWaitingTime = (startDate, status) => {
    // Only show waiting time for checked_in or in_progress status
    if (!['checked_in', 'in_progress'].includes(status)) return null;

    const start = new Date(startDate);
    const now = new Date();
    const diffMs = now - start;

    // If appointment hasn't started yet (future time), show 0
    if (diffMs < 0) return null;

    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;

    if (hours > 0) {
      return `${hours} ${print_lang('hour', 'hr')} ${mins} ${print_lang('minute', 'min')}`;
    }
    return `${mins} ${print_lang('minute', 'min')}`;
  };

  // Get waiting time color based on duration
  const getWaitingTimeColor = (startDate) => {
    const start = new Date(startDate);
    const now = new Date();
    const diffMins = Math.floor((now - start) / 60000);

    if (diffMins < 0) return 'text-gray-500';
    if (diffMins <= 15) return 'text-green-600';  // 0-15 min: Green (good)
    if (diffMins <= 30) return 'text-yellow-600'; // 15-30 min: Yellow (okay)
    if (diffMins <= 60) return 'text-orange-600'; // 30-60 min: Orange (long)
    return 'text-red-600';                         // 60+ min: Red (too long)
  };

  // Update current time every minute for waiting time display
  // eslint-disable-next-line no-unused-vars
  const [currentTime, setCurrentTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date()); // Triggers re-render to update waiting times
    }, 60000); // Update every minute
    return () => clearInterval(timer);
  }, []);

  // Toggle treatment selection
  const toggleTreatment = (treatment) => {
    setSelectedTreatments(prev => {
      const exists = prev.find(t => t.t_id === treatment.t_id);
      if (exists) {
        return prev.filter(t => t.t_id !== treatment.t_id);
      }
      return [...prev, treatment];
    });
  };

  // Calculate total
  const calculateTotal = () => {
    return selectedTreatments.reduce((sum, t) => sum + (t.price || 0), 0);
  };

  // If patient is selected, show treatment view
  if (selectedAppointment && selectedPatient) {
    return (
      <div className="space-y-6">
        {/* Patient Header */}
        <div className="card">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <button onClick={backToQueue} className="btn btn-secondary">
                <ChevronLeft size={20} />
                {print_lang('back_to_queue', 'Back to Queue')}
              </button>

              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <User size={24} className="text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold">{selectedPatient.name}</h2>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    {selectedPatient.phone && (
                      <span className="flex items-center gap-1">
                        <Phone size={14} /> {selectedPatient.phone}
                      </span>
                    )}
                    {selectedPatient.citizen_id && (
                      <span className="flex items-center gap-1">
                        <CreditCard size={14} /> {selectedPatient.citizen_id}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {/* Medical Alerts */}
              {selectedPatient.drug_allergy && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2">
                  <p className="text-sm font-medium text-red-700 flex items-center gap-2">
                    <AlertTriangle size={16} />
                    {print_lang('drug_allergy', 'Drug Allergy')}: {selectedPatient.drug_allergy}
                  </p>
                </div>
              )}

              {selectedPatient.medical_history && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-2">
                  <p className="text-sm font-medium text-orange-700 flex items-center gap-2">
                    <AlertCircle size={16} />
                    {print_lang('medical_history', 'Medical History')}
                  </p>
                </div>
              )}

              {/* Finish Button */}
              <button
                onClick={finishTreatment}
                className="btn btn-primary flex items-center gap-2"
              >
                <CheckCircle size={20} />
                {print_lang('finish_treatment', 'Finish Treatment')}
              </button>
            </div>
          </div>
        </div>

        {/* Odontogram */}
        <Odontogram
          patientId={selectedPatient.patient_id}
          currentBranch={currentBranch}
        />

        {/* Treatment Selection Modal */}
        {showTreatmentModal && (
          <div className="modal-overlay" onClick={() => setShowTreatmentModal(false)}>
            <div className="modal-content w-full max-w-2xl p-6" onClick={e => e.stopPropagation()}>
              <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                <Stethoscope size={24} />
                {print_lang('record_treatment', 'Record Treatment')}
              </h2>

              {/* Treatment List */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">
                  {print_lang('select_treatments', 'Select Treatments Performed')}
                </label>
                <div className="max-h-64 overflow-y-auto border rounded-lg">
                  {treatments.map(treatment => (
                    <div
                      key={treatment.t_id}
                      onClick={() => toggleTreatment(treatment)}
                      className={`flex items-center justify-between p-3 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                        selectedTreatments.find(t => t.t_id === treatment.t_id)
                          ? 'bg-blue-50 border-l-4 border-l-blue-500'
                          : ''
                      }`}
                    >
                      <div>
                        <p className="font-medium">
                          {currentLanguage === 'th' ? (treatment.name_th || treatment.name) : treatment.name}
                        </p>
                        <p className="text-sm text-gray-500">
                          {currentLanguage === 'th' ? (treatment.category_name_th || treatment.category) : treatment.category}
                        </p>
                      </div>
                      <p className="font-medium text-blue-600">
                        ฿{treatment.price?.toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Treatment Notes */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">
                  {print_lang('treatment_notes', 'Treatment Notes')}
                </label>
                <textarea
                  value={treatmentNotes}
                  onChange={(e) => setTreatmentNotes(e.target.value)}
                  rows={3}
                  placeholder={print_lang('treatment_notes_placeholder', 'Enter treatment notes...')}
                  className="w-full"
                />
              </div>

              {/* Selected Treatments Summary */}
              {selectedTreatments.length > 0 && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium mb-2">{print_lang('selected_treatments', 'Selected Treatments')}</h4>
                  <ul className="space-y-1 text-sm">
                    {selectedTreatments.map(t => (
                      <li key={t.t_id} className="flex justify-between">
                        <span>{t.name}</span>
                        <span>฿{t.price?.toLocaleString()}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="border-t mt-2 pt-2 flex justify-between font-bold">
                    <span>{print_lang('total', 'Total')}</span>
                    <span className="text-blue-600">฿{calculateTotal().toLocaleString()}</span>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowTreatmentModal(false)}
                  className="btn btn-secondary"
                >
                  {print_lang('cancel', 'Cancel')}
                </button>
                <button
                  onClick={submitTreatment}
                  disabled={isSaving || selectedTreatments.length === 0}
                  className="btn btn-primary flex items-center gap-2"
                >
                  <CheckCircle size={18} />
                  {isSaving
                    ? print_lang('saving', 'Saving...')
                    : print_lang('complete_and_send_to_billing', 'Complete & Send to Billing')
                  }
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Queue View
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Users size={28} />
          {print_lang('patient_queue', 'Patient Queue')}
        </h1>

        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">
            {print_lang('last_updated', 'Last updated')}: {lastRefresh.toLocaleTimeString()}
          </span>
          <button
            onClick={openWalkInModal}
            className="btn btn-primary flex items-center gap-2"
          >
            <UserPlus size={18} />
            {print_lang('walk_in_patient', 'Walk-in Patient')}
          </button>
          <button
            onClick={fetchQueue}
            className="btn btn-secondary flex items-center gap-2"
          >
            <RefreshCw size={18} />
            {print_lang('refresh', 'Refresh')}
          </button>
        </div>
      </div>

      {/* Queue Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card bg-yellow-50 border-yellow-200">
          <div className="flex items-center gap-3">
            <Clock size={24} className="text-yellow-600" />
            <div>
              <p className="text-2xl font-bold text-yellow-700">
                {queue.filter(a => a.status === 'checked_in').length}
              </p>
              <p className="text-sm text-yellow-600">{print_lang('waiting', 'Waiting')}</p>
            </div>
          </div>
        </div>

        <div className="card bg-blue-50 border-blue-200">
          <div className="flex items-center gap-3">
            <Activity size={24} className="text-blue-600" />
            <div>
              <p className="text-2xl font-bold text-blue-700">
                {queue.filter(a => a.status === 'in_progress').length}
              </p>
              <p className="text-sm text-blue-600">{print_lang('in_progress', 'In Progress')}</p>
            </div>
          </div>
        </div>

        <div className="card bg-gray-50 border-gray-200">
          <div className="flex items-center gap-3">
            <Calendar size={24} className="text-gray-600" />
            <div>
              <p className="text-2xl font-bold text-gray-700">
                {queue.filter(a => a.status === 'scheduled').length}
              </p>
              <p className="text-sm text-gray-600">{print_lang('scheduled', 'Scheduled')}</p>
            </div>
          </div>
        </div>

        <div className="card bg-green-50 border-green-200">
          <div className="flex items-center gap-3">
            <Users size={24} className="text-green-600" />
            <div>
              <p className="text-2xl font-bold text-green-700">{queue.length}</p>
              <p className="text-sm text-green-600">{print_lang('total_today', 'Total Today')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Queue List */}
      <div className="card">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : queue.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Users size={48} className="mx-auto mb-4 text-gray-300" />
            <p>{print_lang('no_patients_in_queue', 'No patients in queue')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {queue.map((appointment) => {
              const statusConfig = getStatusBadge(appointment.status);
              const StatusIcon = statusConfig.icon;

              return (
                <div
                  key={appointment.id}
                  className={`border rounded-lg p-4 transition-all ${
                    appointment.status === 'in_progress'
                      ? 'border-blue-300 bg-blue-50'
                      : appointment.status === 'checked_in'
                        ? 'border-yellow-300 bg-yellow-50'
                        : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    {/* Patient Info */}
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center">
                        <User size={28} className="text-gray-400" />
                      </div>

                      <div>
                        <h3 className="text-lg font-bold text-gray-800">
                          {appointment.patient_name}
                        </h3>
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {formatTime(appointment.start_date)}
                          </span>
                          {appointment.patient_phone && (
                            <span className="flex items-center gap-1">
                              <Phone size={14} />
                              {appointment.patient_phone}
                            </span>
                          )}
                          {appointment.type_name && (
                            <span className="px-2 py-0.5 rounded text-xs"
                                  style={{ backgroundColor: appointment.color + '20', color: appointment.color }}>
                              {currentLanguage === 'th' ? (appointment.type_name_th || appointment.type_name) : appointment.type_name}
                            </span>
                          )}
                          {appointment.dentist_name && (
                            <span className="flex items-center gap-1 text-blue-600">
                              <Stethoscope size={14} />
                              Dr. {appointment.dentist_name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Alerts & Waiting Time */}
                    <div className="flex items-center gap-2">
                      {/* Waiting Time Display */}
                      {calculateWaitingTime(appointment.start_date, appointment.status) && (
                        <div className={`px-3 py-1 bg-gray-100 rounded-full text-sm flex items-center gap-1 font-medium ${getWaitingTimeColor(appointment.start_date)}`}>
                          <Clock size={14} />
                          {print_lang('waiting_time', 'Wait')}: {calculateWaitingTime(appointment.start_date, appointment.status)}
                        </div>
                      )}
                      {appointment.drug_allergy && (
                        <div className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm flex items-center gap-1">
                          <AlertTriangle size={14} />
                          {print_lang('allergy', 'Allergy')}
                        </div>
                      )}
                      {appointment.medical_history && (
                        <div className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm flex items-center gap-1">
                          <FileText size={14} />
                          {print_lang('medical_history', 'History')}
                        </div>
                      )}
                    </div>

                    {/* Status & Actions */}
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-sm flex items-center gap-1 ${statusConfig.bg} ${statusConfig.text}`}>
                        <StatusIcon size={14} />
                        {statusConfig.label}
                      </span>

                      {appointment.status === 'scheduled' && (
                        <button
                          onClick={() => updateStatus(appointment.id, 'checked_in')}
                          className="btn btn-secondary btn-sm"
                        >
                          {print_lang('check_in', 'Check In')}
                        </button>
                      )}

                      {(appointment.status === 'checked_in' || appointment.status === 'in_progress') && (
                        <button
                          onClick={() => startTreatment(appointment)}
                          className="btn btn-primary flex items-center gap-2"
                        >
                          <Play size={16} />
                          {appointment.status === 'in_progress'
                            ? print_lang('continue', 'Continue')
                            : print_lang('start_treatment', 'Start Treatment')
                          }
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Appointment Notes */}
                  {appointment.text && appointment.text !== appointment.patient_name && (
                    <div className="mt-3 pt-3 border-t text-sm text-gray-600">
                      <strong>{print_lang('notes', 'Notes')}:</strong> {appointment.text}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Walk-in Patient Modal */}
      {showWalkInModal && (
        <div className="modal-overlay" onClick={closeWalkInModal}>
          <div className="modal-content w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <UserPlus size={24} className="text-blue-600" />
                {print_lang('walk_in_patient', 'Walk-in Patient')}
              </h2>
              <button onClick={closeWalkInModal} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            {/* Patient Search or New Patient Form */}
            {!showNewPatientForm ? (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium">
                    {print_lang('select_patient', 'Select Patient')} <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowNewPatientForm(true)}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <UserPlus size={16} />
                    {print_lang('new_patient', 'New Patient')}
                  </button>
                </div>
                <div className="relative mb-2">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={patientSearch}
                    onChange={(e) => {
                      setPatientSearch(e.target.value);
                      fetchPatients(e.target.value);
                    }}
                    placeholder={print_lang('search_patient', 'Search patient by name or phone...')}
                    className="w-full pl-10"
                  />
                </div>
                <select
                  value={walkInForm.patient_id}
                  onChange={(e) => setWalkInForm(prev => ({ ...prev, patient_id: e.target.value }))}
                  className="w-full"
                >
                  <option value="">{print_lang('select_patient', '-- Select Patient --')}</option>
                  {patients.map(patient => (
                    <option key={patient.patient_id} value={patient.patient_id}>
                      {patient.name} {patient.phone ? `(${patient.phone})` : ''}
                    </option>
                  ))}
                </select>
                {walkInForm.patient_id && (
                  <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                    {(() => {
                      const p = patients.find(pt => pt.patient_id === parseInt(walkInForm.patient_id));
                      return p ? (
                        <div className="flex items-center gap-2">
                          <User size={16} className="text-blue-600" />
                          <span className="font-medium">{p.name}</span>
                          {p.drug_allergy && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs flex items-center gap-1">
                              <AlertTriangle size={12} /> {print_lang('allergy', 'Allergy')}
                            </span>
                          )}
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}
              </div>
            ) : (
              /* New Patient Registration Form */
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-green-800 flex items-center gap-2">
                    <UserPlus size={18} />
                    {print_lang('register_new_patient', 'Register New Patient')}
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowNewPatientForm(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="space-y-3">
                  {/* Read SmartCard Button */}
                  {currentBranch?.smartcard_enabled === 1 && (
                    <button
                      type="button"
                      onClick={handleReadSmartcard}
                      disabled={isReadingCard}
                      className="w-full py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <CreditCard size={18} />
                      {isReadingCard
                        ? print_lang('reading_smartcard', 'Reading SmartCard...')
                        : print_lang('read_smartcard', 'Read SmartCard')
                      }
                    </button>
                  )}

                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {print_lang('name', 'Name')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newPatientForm.name}
                      onChange={(e) => setNewPatientForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder={print_lang('patient_name', 'Patient name')}
                      className="w-full"
                    />
                  </div>

                  {/* Phone & Citizen ID */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {print_lang('phone', 'Phone')}
                      </label>
                      <input
                        type="tel"
                        value={newPatientForm.phone}
                        onChange={(e) => setNewPatientForm(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="0812345678"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {print_lang('citizen_id', 'Citizen ID')}
                      </label>
                      <input
                        type="text"
                        value={newPatientForm.citizen_id}
                        onChange={(e) => setNewPatientForm(prev => ({ ...prev, citizen_id: e.target.value }))}
                        placeholder="1234567890123"
                        className="w-full"
                      />
                    </div>
                  </div>

                  {/* Gender & DOB */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {print_lang('gender', 'Gender')}
                      </label>
                      <select
                        value={newPatientForm.gender}
                        onChange={(e) => setNewPatientForm(prev => ({ ...prev, gender: e.target.value }))}
                        className="w-full"
                      >
                        <option value="">{print_lang('select_gender', '-- Select --')}</option>
                        <option value="male">{print_lang('male', 'Male')}</option>
                        <option value="female">{print_lang('female', 'Female')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {print_lang('dob', 'Date of Birth')}
                      </label>
                      <input
                        type="date"
                        value={newPatientForm.dob}
                        onChange={(e) => setNewPatientForm(prev => ({ ...prev, dob: e.target.value }))}
                        className="w-full"
                      />
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {print_lang('address', 'Address')}
                    </label>
                    <input
                      type="text"
                      value={newPatientForm.address}
                      onChange={(e) => setNewPatientForm(prev => ({ ...prev, address: e.target.value }))}
                      placeholder={print_lang('address_placeholder', 'Address from ID card')}
                      className="w-full"
                    />
                  </div>

                  {/* Drug Allergy */}
                  <div>
                    <label className="block text-sm font-medium mb-1 text-red-600">
                      {print_lang('drug_allergy', 'Drug Allergy')}
                    </label>
                    <input
                      type="text"
                      value={newPatientForm.drug_allergy}
                      onChange={(e) => setNewPatientForm(prev => ({ ...prev, drug_allergy: e.target.value }))}
                      placeholder={print_lang('drug_allergy_placeholder', 'e.g., Penicillin, Aspirin')}
                      className="w-full"
                    />
                  </div>

                  {/* Medical History */}
                  <div>
                    <label className="block text-sm font-medium mb-1 text-orange-600">
                      {print_lang('medical_history', 'Medical History')}
                    </label>
                    <input
                      type="text"
                      value={newPatientForm.medical_history}
                      onChange={(e) => setNewPatientForm(prev => ({ ...prev, medical_history: e.target.value }))}
                      placeholder={print_lang('medical_history_placeholder', 'e.g., Diabetes, Hypertension')}
                      className="w-full"
                    />
                  </div>

                  {/* Register Button */}
                  <button
                    type="button"
                    onClick={createNewPatient}
                    disabled={isCreatingPatient || !newPatientForm.name.trim()}
                    className="w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <CheckCircle size={18} />
                    {isCreatingPatient
                      ? print_lang('registering', 'Registering...')
                      : print_lang('register_and_select', 'Register & Select')
                    }
                  </button>
                </div>
              </div>
            )}

            {/* Dentist Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                {print_lang('select_dentist', 'Select Dentist')} <span className="text-red-500">*</span>
              </label>
              <select
                value={walkInForm.dentist_id}
                onChange={(e) => setWalkInForm(prev => ({ ...prev, dentist_id: e.target.value }))}
                className="w-full"
                disabled={user?.role === 'dentist'}
              >
                <option value="">{print_lang('select_dentist', '-- Select Dentist --')}</option>
                {dentists.map(dentist => (
                  <option key={dentist.dentist_id} value={dentist.dentist_id}>
                    Dr. {dentist.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Appointment Type */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                {print_lang('appointment_type', 'Appointment Type')}
              </label>
              <select
                value={walkInForm.evtype_id}
                onChange={(e) => setWalkInForm(prev => ({ ...prev, evtype_id: e.target.value }))}
                className="w-full"
              >
                <option value="">{print_lang('general_checkup', 'General Checkup')}</option>
                {appointmentTypes.map(type => (
                  <option key={type.evtype_id} value={type.evtype_id}>
                    {currentLanguage === 'th' ? (type.type_name_th || type.type_name) : type.type_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">
                {print_lang('notes', 'Notes')}
              </label>
              <textarea
                value={walkInForm.notes}
                onChange={(e) => setWalkInForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                placeholder={print_lang('walk_in_notes_placeholder', 'Chief complaint or reason for visit...')}
                className="w-full"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                onClick={closeWalkInModal}
                className="btn btn-secondary"
              >
                {print_lang('cancel', 'Cancel')}
              </button>
              <button
                onClick={createWalkInAppointment}
                disabled={isCreatingWalkIn || !walkInForm.patient_id || !walkInForm.dentist_id}
                className="btn btn-primary flex items-center gap-2"
              >
                <UserPlus size={18} />
                {isCreatingWalkIn
                  ? print_lang('adding', 'Adding...')
                  : print_lang('add_to_queue', 'Add to Queue')
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DentistQueue;
